import type { OptionDefinition, OptionValue } from "./types";

/** Whether an option is a named choice rather than a number. */
export function isChoice(definition: { type?: string } | undefined): definition is Extract<OptionDefinition, { type: "choice" }> {
  return definition?.type === "choice";
}

/**
 * The value a project should hold for an option. A choice falls back to its
 * default when the stored value is missing or not one of its keys (an old
 * saved project, or a choice the pattern no longer offers). Numbers are kept
 * as stored, or the default when missing or not a number.
 */
export function normaliseOptionValue(definition: OptionDefinition, value: unknown): OptionValue {
  if (isChoice(definition)) {
    return typeof value === "string" && Object.hasOwn(definition.choices ?? {}, value) ? value : definition.default;
  }
  return typeof value === "number" && Number.isFinite(value) ? value : definition.default;
}

/** Every option of a specification, filled from `stored` where it holds a valid value. */
export function optionValues(definitions: Record<string, OptionDefinition>, stored: Record<string, unknown> = {}): Record<string, OptionValue> {
  return Object.fromEntries(Object.entries(definitions).map(([id, definition]) => [id, normaliseOptionValue(definition, stored[id])]));
}

/** Text for an option value: the choice label, On/Off for toggles, or the number. */
export function formatOptionValue(definition: OptionDefinition | undefined, value: OptionValue): string {
  if (isChoice(definition)) return definition.choices[String(value)] ?? String(value);
  if (typeof value !== "number") return String(value);
  if (definition?.type === "toggle") return value ? "On" : "Off";
  const text = Number.isInteger(value) ? String(value) : value.toFixed(1);
  return definition?.type === "length" ? `${text} mm` : text;
}
